import { useCallback, useEffect, useRef, useState } from "react";
import { InvoiceFormData } from "@/types";
import { getTotalAmount } from "@/utils/costUtils";
import { useForm } from "react-hook-form";

export function useInvoiceFormChangeTracking({
    methods,
    setTotalAmount
}: {
    methods: ReturnType<typeof useForm<InvoiceFormData>>;
    setTotalAmount: React.Dispatch<React.SetStateAction<number>>;
}) {
    const [isFormChanged, setIsFormChanged] = useState(false);
    const formChangeVersionRef = useRef(0);

    const markFormChanged = useCallback(() => {
        formChangeVersionRef.current += 1;
        setIsFormChanged(true);
    }, []);

    const clearFormChangedIfCurrent = useCallback((savedVersion: number) => {
        if (formChangeVersionRef.current === savedVersion) {
            setIsFormChanged(false);
        }
    }, []);

    const resetFormChanged = useCallback(() => {
        formChangeVersionRef.current = 0;
        setIsFormChanged(false);
    }, []);

    useEffect(() => {
        const subscription = methods.watch(() => {
            setTotalAmount(getTotalAmount(methods.getValues()));
            markFormChanged();
        });

        return () => subscription.unsubscribe();
    }, [methods, setTotalAmount, markFormChanged]);

    return {
        isFormChanged,
        formChangeVersionRef,
        markFormChanged,
        clearFormChangedIfCurrent,
        resetFormChanged
    };
}
